import { signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import {
  formatMoney,
  cents,
  lineRemainingQty,
  orderRemainingCents,
  orderPaidCents,
  isFullyPaid,
  type OrderLineId,
} from "@orderman/shared";
import { draft, recordPayment, sendAll, backToOrder, backToTables, closeActiveTable } from "../state/store.js";
import { payableOf, unsentCount } from "../state/draft.js";

// Ausgewählte (zu zahlende) Menge je Zeile.
const selection = signal<Record<string, number>>({});
// Offene Sicherheitsabfrage vor dem Kassieren.
const confirming = signal<{ amountCents: number; count: number } | null>(null);

function reset(): void {
  selection.value = {};
  confirming.value = null;
}

export function PaymentView() {
  useEffect(() => reset(), []);

  const d = draft.value!;
  const pay = payableOf(d);
  const fully = isFullyPaid(pay);

  // Vollständig bezahlt → Tisch sofort schließen (kein "Tisch abschließen"-Screen,
  // kein Weiterbearbeiten eines abgeschlossenen Tisches).
  useEffect(() => {
    if (fully) closeActiveTable();
  }, [fully]);
  if (fully) return null;

  const perUnit = (lineId: string): number => {
    const dl = d.lines.find((x) => x.line.id === lineId);
    return dl ? dl.line.unitPriceCents + dl.line.modifiers.reduce((s, m) => s + m.priceDeltaCents, 0) : 0;
  };
  const remOf = (lineId: string) => {
    const dl = d.lines.find((x) => x.line.id === lineId)!;
    return lineRemainingQty(pay, dl.line);
  };
  const set = (lineId: string, qty: number) =>
    (selection.value = { ...selection.value, [lineId]: Math.max(0, Math.min(remOf(lineId), qty)) });

  // offene Positionen (noch nicht voll ausgewählt) und ausgewählte
  const lines = d.lines.filter((dl) => dl.line.status !== "voided" && lineRemainingQty(pay, dl.line) > 0);
  // Info-/Notiz-Pseudo-Zeilen tauchen im Bezahl-UI nicht auf — sie fließen automatisch mit
  // der nächsten Zahlung an die Stationen, das soll der Kassier nicht extra anklicken.
  const payLines = lines.filter((dl) => !dl.line.isInfo);
  const openLines = payLines.filter((dl) => (selection.value[dl.line.id] ?? 0) < lineRemainingQty(pay, dl.line));
  const selLines = payLines.filter((dl) => (selection.value[dl.line.id] ?? 0) > 0);
  const infoOpenLines = lines.filter((dl) => dl.line.isInfo);

  const selectedCents = Object.entries(selection.value).reduce((s, [id, q]) => s + perUnit(id) * q, 0);
  const selectedCount = Object.values(selection.value).reduce((s, q) => s + q, 0);
  // Noch nicht an die Stationen gesendete Positionen → „Senden"-Button (vor dem Kassieren).
  const unsent = unsentCount(d);
  const sentIds = new Set(d.lines.filter((dl) => dl.sent).map((dl) => dl.line.id));

  async function doPay(): Promise<void> {
    const c = confirming.value;
    if (!c) return;
    const items = Object.entries(selection.value)
      .filter(([, q]) => q > 0)
      .map(([lineId, qty]) => ({ lineId: lineId as OrderLineId, qty }));
    // Offene Info-Zeilen mitnehmen — sie haben Preis 0, drücken den Betrag nicht hoch,
    // werden aber so endlich in die Druckqueue der Station gespült.
    for (const dl of infoOpenLines) items.push({ lineId: dl.line.id, qty: lineRemainingQty(pay, dl.line) });
    confirming.value = null;
    if (items.length === 0) return;
    await recordPayment(items, "cash");
    selection.value = {};
  }

  return (
    <div class="payview">
      <div class="ordering-head">
        <button class="back" onClick={backToOrder}>
          ‹ Artikel
        </button>
        <span class="table-label">Abrechnung · {d.tableRef}</span>
        <button class="park" onClick={backToTables}>
          Zurückstellen
        </button>
      </div>

      {d.payments.length > 0 && (
        <div class="paid-strip">
          {d.payments.map((p) => (
            <span class="paid-chip" key={p.paymentId}>
              Beleg {p.belegNo ? `#${p.belegNo}` : "•"} · {formatMoney(p.amountCents)} ·{" "}
              {p.method === "cash" ? "Bar" : "Karte"}
            </span>
          ))}
        </div>
      )}

      {lines.length === 0 ? (
        <div class="empty">
          Nichts offen.
          <br />
          <button class="btn-primary" onClick={closeActiveTable} style="margin-top:16px">
            Tisch schließen
          </button>
        </div>
      ) : (
        <>
          {/* OBEN: offene Positionen */}
          <div class="pay-area open">
            <div class="area-head">
              <span>Offen</span>
              <span class="area-acts">
                {unsent > 0 && (
                  <button class="area-act send" onClick={() => void sendAll()} title="alles an die Stationen senden, später kassieren">
                    Senden
                  </button>
                )}
                <button
                  class="area-act"
                  disabled={openLines.length === 0}
                  onClick={() => {
                    const all: Record<string, number> = { ...selection.value };
                    for (const dl of payLines) all[dl.line.id] = lineRemainingQty(pay, dl.line);
                    selection.value = all;
                  }}
                >
                  Alles ⇊
                </button>
              </span>
            </div>
            <div class="area-lines">
              {openLines.map((dl) => {
                const open = lineRemainingQty(pay, dl.line) - (selection.value[dl.line.id] ?? 0);
                return (
                  <div class="psel-line" key={dl.line.id}>
                    <button
                      class="psel-main"
                      onClick={() => set(dl.line.id, (selection.value[dl.line.id] ?? 0) + 1)}
                      title="ein Stück auswählen"
                    >
                      <span class="psel-qty">{open}×</span>
                      <span class="psel-name">
                        {dl.line.name}
                        {dl.line.modifiers.length > 0 && (
                          <span class="psel-mods">{dl.line.modifiers.map((m) => m.name).join(", ")}</span>
                        )}
                        {sentIds.has(dl.line.id) && <span class="sent-tag">✓ gesendet</span>}
                      </span>
                      <span class="psel-price">{formatMoney(cents(perUnit(dl.line.id)))}</span>
                    </button>
                    <button
                      class="psel-one"
                      onClick={() => set(dl.line.id, lineRemainingQty(pay, dl.line))}
                      title="ganze Position auswählen"
                    >
                      ⇊
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* UNTEN: zu zahlen */}
          <div class="pay-area to-pay">
            <div class="area-head">
              <span>Zu zahlen</span>
              {selLines.length > 0 && (
                <button class="area-act ghost" onClick={() => (selection.value = {})}>
                  zurück ⇈
                </button>
              )}
            </div>
            <div class="area-lines">
              {selLines.length === 0 ? (
                <div class="area-empty">Positionen oben antippen…</div>
              ) : (
                selLines.map((dl) => {
                  const q = selection.value[dl.line.id] ?? 0;
                  return (
                    <div class="psel-line sel" key={dl.line.id}>
                      <button class="psel-main" onClick={() => set(dl.line.id, q - 1)} title="ein Stück zurück">
                        <span class="psel-qty">{q}×</span>
                        <span class="psel-name">
                        {dl.line.name}
                        {dl.line.modifiers.length > 0 && (
                          <span class="psel-mods">{dl.line.modifiers.map((m) => m.name).join(", ")}</span>
                        )}
                      </span>
                        <span class="psel-price">{formatMoney(cents(perUnit(dl.line.id) * q))}</span>
                      </button>
                      <button class="psel-one" onClick={() => set(dl.line.id, 0)} title="ganze Position zurück">
                        ⇈
                      </button>
                    </div>
                  );
                })
              )}
            </div>
            <div class="pay-foot">
              <div class="pay-summary">
                <b>{formatMoney(cents(selectedCents))}</b>
                <span class="muted">offen gesamt {formatMoney(orderRemainingCents(pay))}</span>
              </div>
              <div class="pay-actions">
                <button
                  class="pay-method cash"
                  disabled={selectedCount === 0}
                  onClick={() => (confirming.value = { amountCents: selectedCents, count: selectedCount })}
                >
                  Kassieren
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Sicherheitsabfrage */}
      {confirming.value && (
        <div class="modal-backdrop" onClick={() => (confirming.value = null)}>
          <div class="confirm" onClick={(e) => e.stopPropagation()}>
            <div class="confirm-q">Kassieren?</div>
            <div class="confirm-amount">{formatMoney(cents(confirming.value.amountCents))}</div>
            <div class="confirm-meta">{confirming.value.count} Position(en) · Bar</div>
            <div class="confirm-actions">
              <button class="btn-secondary" onClick={() => (confirming.value = null)}>
                Abbrechen
              </button>
              <button class="btn-primary" onClick={() => void doPay()}>
                Ja, kassieren
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
